import React, { useEffect, useMemo, useState } from 'react';
import {Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import {useNavigateBack, getLocationReturnPath, openRecord } from '../../../hooks/useNavigateBack';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../lib/utils';
import {
  Button,
  DateInput,
  Label,
  NumberInput,
  Select,
  Switch,
  Textarea,
  Badge,
} from '../../../components/ui';
import { createCrewWageScale, updateCrewWageScale } from '../../../services/mutations';
import { useCrewWageLookups, useCrewWageScale } from './useData';
import { moduleConfig } from './config';
import type { CrewContractColumn } from '../crew-wage-columns/useData';

const todayIso = () => new Date().toISOString().slice(0, 10);

const TYPE_BADGE: Record<string, 'success' | 'danger' | 'secondary'> = {
  EARNING: 'success',
  DEDUCTION: 'danger',
  INFO: 'secondary',
};

const TYPE_ORDER: Record<string, number> = { EARNING: 0, DEDUCTION: 1, INFO: 2 };

function formatAmount(n: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
}

const Form: React.FC = () => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useNavigateBack(moduleConfig.module.route.list, getLocationReturnPath(location.state));
  const { id } = useParams<{ id: string }>();
  const editId = id && id !== 'new' ? parseInt(id, 10) : NaN;
  const isEdit = Number.isInteger(editId);

  const canEdit = can(moduleConfig.module.permissionModuleKey, 'edit');
  const { lookups, isLoading: loadingLookups } = useCrewWageLookups();
  const { item, isLoading: loadingItem } = useCrewWageScale(isEdit ? editId : 0);

  const [vesselId, setVesselId] = useState('');
  const [rankId, setRankId] = useState('');
  const [nationalityId, setNationalityId] = useState('');
  const [currency, setCurrency] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso);
  const [effectiveTo, setEffectiveTo] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(!isEdit);

  const allColumns = useMemo(() => {
    const cols = lookups?.contractColumns ?? [];
    return cols
      .slice()
      .sort(
        (a, b) =>
          (TYPE_ORDER[a.columnType] ?? 9) - (TYPE_ORDER[b.columnType] ?? 9) ||
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
          a.name.localeCompare(b.name),
      );
  }, [lookups?.contractColumns]);

  /** Active columns for entry; inactive still shown if they already have a saved amount. */
  const visibleColumns = useMemo(() => {
    return allColumns.filter((c) => c.isActive !== false || (amounts[c.id] !== undefined && amounts[c.id] !== ''));
  }, [allColumns, amounts]);

  useEffect(() => {
    if (!isEdit || !item || hydrated) return;
    setVesselId(String(item.vesselId));
    setRankId(String(item.rankId));
    setNationalityId(String(item.nationalityId));
    setCurrency(item.currency ?? '');
    setEffectiveFrom(item.effectiveFrom?.slice(0, 10) ?? todayIso());
    setEffectiveTo(item.effectiveTo?.slice(0, 10) ?? '');
    setIsActive(item.isActive ?? true);
    setNotes(item.notes ?? '');
    const next: Record<number, string> = {};
    for (const v of item.values ?? []) {
      next[v.columnId] = v.amount != null ? String(v.amount) : '';
    }
    setAmounts(next);
    setHydrated(true);
  }, [isEdit, item, hydrated]);

  useEffect(() => {
    if (isEdit || !allColumns.length) return;
    setAmounts((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const next: Record<number, string> = {};
      for (const col of allColumns) {
        if (col.isActive !== false) next[col.id] = '';
      }
      return next;
    });
  }, [isEdit, allColumns]);

  const vesselOptions = useMemo(
    () => (lookups?.vessels ?? []).map((v) => ({ value: String(v.id), label: v.vesselName })),
    [lookups?.vessels],
  );
  const rankOptions = useMemo(
    () =>
      (lookups?.ranks ?? []).map((r) => ({
        value: String(r.id),
        label: r.name,
      })),
    [lookups?.ranks],
  );
  const nationalityOptions = useMemo(
    () => (lookups?.nationalities ?? []).map((n) => ({ value: String(n.id), label: n.name })),
    [lookups?.nationalities],
  );
  const currencyOptions = useMemo(
    () =>
      (lookups?.currencies ?? []).map((c) => ({
        value: c.code,
        label: c.symbol ? `${c.name} (${c.symbol})` : c.name,
      })),
    [lookups?.currencies],
  );

  const vesselLabel = vesselOptions.find((o) => o.value === vesselId)?.label;
  const rankLabel = rankOptions.find((o) => o.value === rankId)?.label;
  const nationalityLabel = nationalityOptions.find((o) => o.value === nationalityId)?.label;

  const computedTotal = useMemo(() => {
    let total = 0;
    for (const col of allColumns) {
      if (!col.includeInTotal || col.columnType === 'INFO') continue;
      const raw = amounts[col.id];
      if (raw === undefined || raw === '') continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) continue;
      total += col.columnType === 'DEDUCTION' ? -n : n;
    }
    return Math.round(total * 100) / 100;
  }, [allColumns, amounts]);

  const columnsByType = useMemo(() => {
    const groups: { type: string; label: string; cols: CrewContractColumn[] }[] = [
      { type: 'EARNING', label: 'Earnings', cols: [] },
      { type: 'DEDUCTION', label: 'Deductions', cols: [] },
      { type: 'INFO', label: 'Info', cols: [] },
    ];
    for (const col of visibleColumns) {
      const g = groups.find((x) => x.type === col.columnType) ?? groups[2];
      g.cols.push(col);
    }
    return groups.filter((g) => g.cols.length > 0);
  }, [visibleColumns]);

  const setAmount = (columnId: number, value: string) => {
    setAmounts((prev) => ({ ...prev, [columnId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    if (!vesselId || !rankId || !nationalityId) {
      showError('Vessel, rank and nationality are required.');
      return;
    }

    const values = allColumns
      .map((col) => {
        const raw = amounts[col.id];
        if (raw === undefined || raw === '') return null;
        const amount = Number(raw);
        if (!Number.isFinite(amount)) return null;
        return { columnId: col.id, amount };
      })
      .filter((v): v is { columnId: number; amount: number } => v != null);

    const payload = {
      vesselId: Number(vesselId),
      rankId: Number(rankId),
      nationalityId: Number(nationalityId),
      currency: currency || null,
      effectiveFrom: effectiveFrom || null,
      effectiveTo: effectiveTo || null,
      isActive,
      notes: notes.trim() || null,
      values,
    };

    setSaving(true);
    try {
      const from = getLocationReturnPath(location.state) ?? moduleConfig.module.route.list;
      if (isEdit) {
        await updateCrewWageScale(editId, payload);
        showSuccess('Wage scale saved.');
        openRecord(navigate, moduleConfig.module.route.detail(editId), from);
      } else {
        const created = await createCrewWageScale(payload);
        showSuccess('Wage scale created.');
        openRecord(navigate, moduleConfig.module.route.detail(created.id), from);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      const text = Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : 'Save failed.';
      showError(text);
    } finally {
      setSaving(false);
    }
  };

  const loading = loadingLookups || (isEdit && (loadingItem || !hydrated));

  if (loading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (isEdit && !item) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>Wage scale not found</h1>
        <Link to={moduleConfig.module.route.list} className="mt-4 inline-block text-sm text-blue-600 underline">
          Back to wage scales
        </Link>
      </div>
    );
  }

  return (
    <div className={cn('min-h-full', colors.background)}>
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <Link
              to={isEdit ? moduleConfig.module.route.detail(editId) : moduleConfig.module.route.list}
              className={cn('text-sm font-medium', colors.textSecondary, 'hover:underline')}
            >
              ← {isEdit ? 'Back to details' : 'Back to wage scales'}
            </Link>
            <h1 className={cn('mt-2 text-2xl font-semibold tracking-tight', colors.text)}>
              {isEdit ? 'Edit wage scale' : 'New wage scale'}
            </h1>
            <p className={cn('mt-1 max-w-2xl text-sm', colors.textSecondary)}>
              Set the wage split for a vessel, rank and nationality. These amounts import onto S/On.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={goBack}>
              Cancel
            </Button>
            {canEdit && (
              <Button type="submit" form="wage-scale-form" disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create scale'}
              </Button>
            )}
          </div>
        </div>

        <form id="wage-scale-form" onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-12">
          <div className="space-y-5 lg:col-span-7">
            <section className={cn('rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <div className="mb-4">
                <h2 className={cn('text-base font-semibold', colors.text)}>Scope</h2>
                <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                  Who this rate card applies to, and when it is valid.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-12">
                <div className="sm:col-span-6">
                  <Label htmlFor="vesselId" required>
                    Vessel
                  </Label>
                  <Select
                    name="vesselId"
                    value={vesselId}
                    onChange={(e) => setVesselId(e.target.value)}
                    options={vesselOptions}
                    placeholder="Select vessel"
                    required
                    showClearButton={false}
                    disabled={!canEdit}
                  />
                </div>
                <div className="sm:col-span-6">
                  <Label htmlFor="rankId" required>
                    Rank
                  </Label>
                  <Select
                    name="rankId"
                    value={rankId}
                    onChange={(e) => setRankId(e.target.value)}
                    options={rankOptions}
                    placeholder="Select rank"
                    required
                    showClearButton={false}
                    disabled={!canEdit}
                  />
                </div>
                <div className="sm:col-span-6">
                  <Label htmlFor="nationalityId" required>
                    Nationality
                  </Label>
                  <Select
                    name="nationalityId"
                    value={nationalityId}
                    onChange={(e) => setNationalityId(e.target.value)}
                    options={nationalityOptions}
                    placeholder="Select nationality"
                    required
                    showClearButton={false}
                    disabled={!canEdit}
                  />
                </div>
                <div className="sm:col-span-6">
                  <Label htmlFor="currency">Currency</Label>
                  <Select
                    name="currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    options={currencyOptions}
                    placeholder="Select currency"
                    disabled={!canEdit}
                  />
                </div>
                <div className="sm:col-span-6">
                  <Label htmlFor="effectiveFrom">Effective from</Label>
                  <DateInput
                    name="effectiveFrom"
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                <div className="sm:col-span-6">
                  <Label htmlFor="effectiveTo">Effective to</Label>
                  <DateInput
                    name="effectiveTo"
                    value={effectiveTo}
                    onChange={(e) => setEffectiveTo(e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                <div className="sm:col-span-12">
                  <div className={cn('rounded-lg border px-4 py-3', colors.border)}>
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={!canEdit}
                      label="Active"
                      description="Inactive scales are not offered on S/On import."
                    />
                  </div>
                </div>
                <div className="sm:col-span-12">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    name="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Optional notes (CBA reference, remarks…)"
                    disabled={!canEdit}
                    maxLength={250}
                    showCharacterCount
                  />
                </div>
              </div>
            </section>

            <section className={cn('rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h2 className={cn('text-base font-semibold', colors.text)}>Wage split amounts</h2>
                  <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                    Enter amounts for each wage column. Leave blank if not used on this scale.
                  </p>
                </div>
                <Link
                  to="/parameters/crew/wage-columns"
                  className="text-sm font-medium text-blue-600 hover:underline"
                >
                  Manage columns
                </Link>
              </div>

              {!visibleColumns.length ? (
                <div className={cn('rounded-lg border border-dashed px-4 py-8 text-center', colors.border)}>
                  <p className={cn('text-sm', colors.textMuted)}>No wage columns defined yet.</p>
                  <Link
                    to="/parameters/crew/wage-columns/new"
                    className="mt-2 inline-block text-sm font-medium text-blue-600 hover:underline"
                  >
                    Create a wage column
                  </Link>
                </div>
              ) : (
                <div className="space-y-5">
                  {columnsByType.map((group) => (
                    <div key={group.type}>
                      <div className="mb-2 flex items-center gap-2">
                        <Badge
                          variant={TYPE_BADGE[group.type] ?? 'secondary'}
                          className="rounded-md px-2 py-0.5"
                        >
                          {group.label}
                        </Badge>
                        <span className={cn('text-xs', colors.textMuted)}>
                          {group.cols.length} column{group.cols.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className={cn('overflow-hidden rounded-lg border', colors.border)}>
                        <table className="min-w-full text-sm">
                          <thead className={colors.background}>
                            <tr>
                              <th className={cn('px-3 py-2 text-left font-medium', colors.textMuted)}>Code</th>
                              <th className={cn('px-3 py-2 text-left font-medium', colors.textMuted)}>Name</th>
                              <th className={cn('w-44 px-3 py-2 text-right font-medium', colors.textMuted)}>
                                Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.cols.map((col) => (
                              <tr
                                key={col.id}
                                className={cn(
                                  'border-t',
                                  colors.border,
                                  col.isActive === false ? 'opacity-60' : '',
                                )}
                              >
                                <td className={cn('px-3 py-2.5 font-mono text-xs', colors.textMuted)}>
                                  {col.code}
                                </td>
                                <td className={cn('px-3 py-2.5', colors.text)}>
                                  <div className="font-medium">{col.name}</div>
                                  {col.isActive === false && (
                                    <div className={cn('text-xs', colors.textMuted)}>Inactive column</div>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <NumberInput
                                    name={`amount-${col.id}`}
                                    value={amounts[col.id] ?? ''}
                                    onChange={(e) =>
                                      setAmount(
                                        col.id,
                                        e.target.value == null ? '' : String(e.target.value),
                                      )
                                    }
                                    step={col.dataType === 'INTEGER' ? 1 : 0.01}
                                    allowDecimals={col.dataType !== 'INTEGER'}
                                    className="text-right"
                                    disabled={!canEdit}
                                    showClearButton={false}
                                    placeholder="—"
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="lg:col-span-5">
            <div className={cn('sticky top-6 space-y-4 rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <div>
                <h2 className={cn('text-base font-semibold', colors.text)}>Summary</h2>
                <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                  Preview of what will import on S/On for this match.
                </p>
              </div>

              <div className={cn('space-y-2 rounded-lg border p-4', colors.border, colors.background)}>
                <div className="flex justify-between gap-2 text-sm">
                  <span className={colors.textMuted}>Vessel</span>
                  <span className={cn('text-right font-medium', colors.text)}>{vesselLabel || '—'}</span>
                </div>
                <div className="flex justify-between gap-2 text-sm">
                  <span className={colors.textMuted}>Rank</span>
                  <span className={cn('text-right font-medium', colors.text)}>{rankLabel || '—'}</span>
                </div>
                <div className="flex justify-between gap-2 text-sm">
                  <span className={colors.textMuted}>Nationality</span>
                  <span className={cn('text-right font-medium', colors.text)}>{nationalityLabel || '—'}</span>
                </div>
                <div className="flex justify-between gap-2 text-sm">
                  <span className={colors.textMuted}>Currency</span>
                  <span className={cn('text-right font-medium', colors.text)}>{currency || '—'}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Badge
                    variant={isActive ? 'success' : 'danger'}
                    className="rounded-md px-2 py-0.5"
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  {(effectiveFrom || effectiveTo) && (
                    <span className={cn('text-xs', colors.textMuted)}>
                      {effectiveFrom || '…'} → {effectiveTo || 'open'}
                    </span>
                  )}
                </div>
              </div>

              <div className={cn('rounded-lg border p-4', colors.border)}>
                <div className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>
                  Contract total
                </div>
                <div className={cn('mt-1 text-2xl font-semibold tabular-nums', colors.text)}>
                  {formatAmount(computedTotal)}
                  {currency ? (
                    <span className={cn('ml-1.5 text-base font-medium', colors.textMuted)}>{currency}</span>
                  ) : null}
                </div>
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  Earnings minus deductions (columns marked include-in-total).
                </p>
              </div>

              <ul className={cn('space-y-2 text-sm', colors.textSecondary)}>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                  Exact match: vessel + rank + nationality (+ effective dates).
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                  On S/On, user imports this scale then can override amounts.
                </li>
              </ul>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default Form;
